/**
 * 0128 · A TRAVA DO LANÇAMENTO DE ABASTECIMENTO, na tela de Papéis — a conta,
 * sem desenho.
 *
 * O dono (27/10/2026): *"criar a POSSIBILIDADE de fazer isso, e nos parâmetros
 * do papel, no sistema, aí sim criar a trava diretamente no papel"*. Nenhuma
 * trava vem do código: o papel nasce sem trava, e quem administra a configura
 * aqui, com motivo escrito.
 *
 * O que esta tela precisa dizer, e por que é conta e não frase solta: a trava
 * é do PAPEL, mas o que a pessoa lança é a UNIÃO dos papéis dela que lançam —
 * e um papel sem trava libera tudo. Sem a lista de quem o outro papel libera,
 * quem trava acha que travou e não travou.
 */
import type { Papel, PapelDaPessoa, Pessoa } from './tipos';

export type TipoDaTrava = 'ambos' | 'interno' | 'externo';
export type TanquesDaTrava = 'todos' | 'marcados';

/** O formulário da trava, como a pessoa o vê. */
export interface FormularioDaTrava {
  readonly tipo: TipoDaTrava;
  readonly tanques: TanquesDaTrava;
  readonly marcados: readonly string[];
}

/** O que o papel grava hoje, na forma do formulário. */
export function formularioDoPapel(papel: Pick<Papel, 'abastecimento_tipos' | 'abastecimento_tanques'>): FormularioDaTrava {
  const tipos = papel.abastecimento_tipos ?? null;
  const tanques = papel.abastecimento_tanques ?? null;
  return {
    tipo: tipos === null || tipos.length !== 1 ? 'ambos' : (tipos[0] ?? 'ambos'),
    tanques: tanques === null ? 'todos' : 'marcados',
    marcados: tanques ?? [],
  };
}

/** O corpo do envio. Os dois tipos com todos os tanques é "sem trava": vai nulo. */
export function corpoDaTrava(f: FormularioDaTrava): {
  readonly tipos: readonly ('interno' | 'externo')[] | null;
  readonly tanques: readonly string[] | null;
} {
  const tipos = f.tipo === 'ambos' ? null : [f.tipo];
  /* O tanque só trava o interno: no papel só do posto ele não vai. */
  const tanques = f.tipo === 'externo' || f.tanques === 'todos' ? null : f.marcados;
  return { tipos, tanques };
}

/** O que falta para gravar, ou `null`. Diz o que fazer (p.61). */
export function faltaNaTrava(f: FormularioDaTrava): string | null {
  if (f.tipo !== 'externo' && f.tanques === 'marcados' && f.marcados.length === 0) {
    return 'Marque pelo menos um tanque, ou deixe todos os tanques liberados.';
  }
  return null;
}

/**
 * A trava escrita, para a lista, o toast e a pessoa: "Só interno · do Comboio
 * de teste", "Interno e externo · 2 tanques", "Sem trava".
 */
export function travaEscrita(
  tipos: readonly string[] | null | undefined,
  tanques: readonly string[] | null | undefined,
  nomeDoTanque: (id: string) => string | undefined,
): string {
  const t = tipos ?? null;
  const k = tanques ?? null;
  if (t === null && k === null) return 'Sem trava';
  const tipo = t === null || t.length !== 1 ? 'Interno e externo' : t[0] === 'interno' ? 'Só interno' : 'Só externo';
  /* Só no posto, o tanque não se aplica. */
  if (t !== null && !t.includes('interno')) return tipo;
  if (k === null) return `${tipo} · de qualquer tanque`;
  if (k.length === 0) return `${tipo} · de nenhum tanque`;
  /* Sem o nome (a lista de tanques ainda vem, ou a pessoa não lê o
     combustível), o número — e nunca um palpite sobre o tanque. */
  if (k.length === 1) {
    const nome = nomeDoTanque(k[0] ?? '');
    return nome === undefined ? `${tipo} · 1 tanque` : `${tipo} · do ${nome}`;
  }
  return `${tipo} · ${String(k.length)} tanques`;
}

/**
 * QUEM O OUTRO PAPEL LIBERA: as pessoas com este papel, vigente, para quem a
 * trava NÃO vale — o banco diz (`trava_do_lancamento` nula) que outro papel
 * delas lança sem trava. Só faz sentido quando este papel tem trava.
 */
export function liberadasPorOutroPapel(
  papel: Pick<Papel, 'id' | 'abastecimento_tipos' | 'abastecimento_tanques'>,
  vinculos: readonly Pick<PapelDaPessoa, 'usuario_id' | 'papel_id' | 'vigente'>[],
  pessoas: readonly Pick<Pessoa, 'id' | 'nome' | 'ativo' | 'trava_do_lancamento'>[],
): readonly string[] {
  if ((papel.abastecimento_tipos ?? null) === null && (papel.abastecimento_tanques ?? null) === null) return [];
  const com = new Set(vinculos.filter((v) => v.papel_id === papel.id && v.vigente !== false).map((v) => v.usuario_id));
  return pessoas
    .filter((p) => p.ativo && com.has(p.id) && (p.trava_do_lancamento ?? null) === null)
    .map((p) => p.nome)
    .sort((x, y) => x.localeCompare(y, 'pt-BR'));
}

/** Quantas pessoas, ativas e com o papel vigente, a trava alcança. */
export function pessoasComOPapel(
  papelId: string,
  vinculos: readonly Pick<PapelDaPessoa, 'usuario_id' | 'papel_id' | 'vigente'>[],
  pessoas: readonly Pick<Pessoa, 'id' | 'ativo'>[],
): number {
  const com = new Set(vinculos.filter((v) => v.papel_id === papelId && v.vigente !== false).map((v) => v.usuario_id));
  return pessoas.filter((p) => p.ativo && com.has(p.id)).length;
}

/**
 * O QUE VALE PARA A PESSOA, escrito — a mesa e o celular mostram o mesmo.
 *
 * `null` quando nenhum papel dela lança (não há o que dizer). O aviso só
 * aparece quando um papel trava e outro, sem trava, libera: é o caso em que
 * quem olha a lista de papéis conclui o contrário do que vale.
 */
export function travaDaPessoaEscrita(
  trava: { readonly tipos: readonly string[] | null; readonly tanques: readonly string[] | null } | null | undefined,
  papeisDaPessoa: readonly Pick<Papel, 'nome' | 'lanca_abastecimento' | 'abastecimento_tipos' | 'abastecimento_tanques'>[],
  nomeDoTanque: (id: string) => string | undefined,
): { readonly texto: string; readonly aviso: string | null } | null {
  const queLancam = papeisDaPessoa.filter((p) => p.lanca_abastecimento === true);
  if (queLancam.length === 0) return null;
  const temTrava = (p: (typeof queLancam)[number]) => (p.abastecimento_tipos ?? null) !== null || (p.abastecimento_tanques ?? null) !== null;
  const travados = queLancam.filter(temTrava).map((p) => p.nome);
  const livres = queLancam.filter((p) => !temTrava(p)).map((p) => p.nome);
  const vale = trava ?? null;
  return {
    texto: vale === null ? 'Sem trava' : travaEscrita(vale.tipos, vale.tanques, nomeDoTanque),
    aviso:
      vale === null && travados.length > 0 && livres.length > 0
        ? `${travados.join(', ')} ${travados.length === 1 ? 'tem' : 'têm'} trava, mas ${livres.join(', ')} lança sem trava e libera tudo. Para a trava valer, tire ${livres.length === 1 ? 'esse papel' : 'esses papéis'} ou trave-${livres.length === 1 ? 'o' : 'os'} também.`
        : null,
  };
}
